import React, { useEffect, lazy, Suspense } from "react";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { getLocalizedUrl, parseLocalizedUrl, isActiveUrl } from "@/lib/localized-routes";
import { PageLoadingSpinner, FullscreenLoadingSpinner } from "@/components/ui/loading-spinner";

// Initialize i18n
import "./lib/i18n";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ApiErrorBoundary } from "@/components/ApiErrorBoundary";
import { PWAInstallButton, PWAUpdateNotification, PWAMobileInstructions } from "@/components/pwa-install-button";
import { SystemSettingsProvider, useSystemSettings } from "@/hooks/use-system-settings";
import { OnboardingProvider } from "@/components/onboarding/onboarding-provider";
// OnboardingTooltipRenderer removed - now handled directly in OnboardingProvider
import { ProtectedRoute, PermissionProtectedRoute } from "@/lib/protected-route";
import { I18nProvider } from "@/components/I18nProvider";
import i18n from "@/lib/i18n";

import { MainLayout } from "@/components/layout/main-layout";
import NotFound from "@/pages/not-found";
import AuthPage from "@/pages/auth-page";
// Demo banner disabled

// PWA utilities - ServiceWorker disabled to fix conflicts
import { checkInstallPrompt, addPWAListeners } from "@/utils/pwa-utils";

// Eagerly loaded components (frequently used)
import Dashboard from "@/pages/dashboard";
import Clients from "@/pages/clients";
import Tasks from "@/pages/tasks";
import Invoices from "@/pages/invoices";
import InvoicesSimple from "@/pages/invoices-simple";
import PriceQuotes from "@/pages/price-quotes";
import DobropisyPage from "@/pages/dobropisy-page";
import Profile from "@/pages/profile";
import Upgrade from "@/pages/upgrade";
import SharedTask from "@/pages/shared-task";
import SharedClientTasks from "@/pages/shared-client-tasks";
import ClientTasks from "@/pages/client-tasks";
import ResetPassword from "@/pages/reset-password";
import TeamInvitationPage from "@/pages/team-invitation";
import TeamManagementPage from "@/pages/team-management";
import RegistrationSuccessPage from "@/pages/registration-success";
import EmailExistsPage from "@/pages/email-exists";
import WeeklyPlanner from "@/pages/weekly-planner";
import StandalonePlanner from "@/pages/standalone-planner";
import PersonalTasksPage from "@/pages/personal-tasks-page";
import Pricing from "@/pages/pricing";
import Subscribe from "@/pages/subscribe";
import TestLogin from "@/pages/TestLogin";
import InvoicesTest from "@/pages/InvoicesTest";
import RemindersPage from "@/pages/reminders";

// Lazy loaded components (admin pages - heavy components)
const Admin = lazy(() => import("@/pages/admin"));
const AdminDiagnosticsPage = lazy(() => import("@/pages/admin/admin-diagnostics"));
const SubscriptionPlansPage = lazy(() => import("@/pages/admin/subscription-plans"));
const UpdateCenterPage = lazy(() => import("@/pages/admin/update-center"));
const EnterpriseOverview = lazy(() => import("@/pages/admin/EnterpriseOverview"));

// Komponent pre nastavenie a kontrolu motívu (dark/light/system) - používa sa v SystemSettingsProvider
function ThemeWatcher() {
  useEffect(() => {
    const themeFromLocalStorage = localStorage.getItem("theme");
    const themeFromSystem = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    
    // Nastavenie dark/light triedy na HTML elemente
    const applyTheme = (theme: string) => {
      document.documentElement.classList.remove("light", "dark");
      
      // Ak je nastavený system, použijeme systémovú tému
      if (theme === "system") {
        document.documentElement.classList.add(themeFromSystem);
      } else {
        document.documentElement.classList.add(theme);
      }
    };

    // Aplikujeme tému - ak nie je nastavená, použijeme systémovú
    applyTheme(themeFromLocalStorage || "system");
    
    // Nastav HTML lang podľa i18n – i18n.ts už počúva languageChanged a udržiava to v sync
    document.documentElement.lang = i18n.language || 'sk';

    // Sledovanie zmien systémového nastavenia
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      const currentTheme = localStorage.getItem("theme") || "system";
      if (currentTheme === "system") {
        applyTheme("system");
      }
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return null;
}

// ThemeManager removed - was causing initialization issues

function Router() {
  const [location] = useLocation();
  
  // Parse the current location to get language and route
  const { route: currentRoute, language: currentLanguage } = parseLocalizedUrl(location);
  
  // Určíme, ktoré stránky používajú layout
  const isAuthPage = currentRoute === "/auth";
  const isRegistrationSuccessPage = currentRoute === "/registration-success";
  const isEmailExistsPage = currentRoute === "/email-exists";
  const isResetPasswordPage = currentRoute.startsWith("/reset-password");
  const isSharedTaskPage = currentRoute.startsWith("/shared-task/");
  const isSharedClientTasksPage = currentRoute.startsWith("/shared-client-tasks/");
  const isClientTasksPage = currentRoute.startsWith("/client/") && currentRoute.includes("/tasks");
  const isStandalonePlannerPage = currentRoute === "/standalone-planner";
  const isTeamInvitationPage = currentRoute.startsWith("/team-invitation/");
  const isOnboardingPage = currentRoute === "/onboarding";

  // Stránky, ktoré nepotrebujú layout
  const noLayoutPaths = [isAuthPage, isResetPasswordPage, isSharedTaskPage, isSharedClientTasksPage, isClientTasksPage, isStandalonePlannerPage, isTeamInvitationPage, isOnboardingPage, isRegistrationSuccessPage, isEmailExistsPage].some(Boolean);
  
  // Logujeme aktuálne informácie pre ladenie
  // Routing and layout logic

  if (noLayoutPaths) {
    return (
      <div>
        {isAuthPage && <AuthPage />}
        {isRegistrationSuccessPage && (
          <Route path="/registration-success">
            {() => {
              const params = new URLSearchParams(window.location.search);
              const email = params.get('email') || '';
              return <RegistrationSuccessPage email={email} />;
            }}
          </Route>
        )}
        {isEmailExistsPage && (
          <Route path="/email-exists">
            {() => {
              const params = new URLSearchParams(window.location.search);
              const email = params.get('email') || '';
              return <EmailExistsPage email={email} />;
            }}
          </Route>
        )}
        {isResetPasswordPage && <ResetPassword />}
        {isSharedTaskPage && <SharedTask />}
        {isSharedClientTasksPage && <SharedClientTasks />}
        {isClientTasksPage && <ClientTasks />}
        {isStandalonePlannerPage && <StandalonePlanner />}
        {isTeamInvitationPage && <TeamInvitationPage />}
        {isOnboardingPage && <Redirect to={getLocalizedUrl("/dashboard")} />}
        {location === "/test-login" && <TestLogin />}
        {!isAuthPage && !isResetPasswordPage && !isSharedTaskPage && !isSharedClientTasksPage && !isClientTasksPage && !isStandalonePlannerPage && !isTeamInvitationPage && !isOnboardingPage && !isRegistrationSuccessPage && !isEmailExistsPage && location !== "/test-login" && <NotFound />}
      </div>
    );
  }
  
  return (
    <MainLayout>
      <Switch>
        {/* Root route - redirect to auth if not logged in */}
        <Route path="/">
          {() => {
            const { user, isLoading } = useAuth();
            if (isLoading) return <FullscreenLoadingSpinner />;
            if (!user) return <Redirect to="/auth" />;
            return <Dashboard />;
          }}
        </Route>
        {/* Dashboard routes */}
        <PermissionProtectedRoute path="/dashboard" component={Dashboard} requiredResource="dashboard" />
        <PermissionProtectedRoute path="/app/dashboard" component={Dashboard} requiredResource="dashboard" />
        <PermissionProtectedRoute path="/nastenka" component={Dashboard} requiredResource="dashboard" />
        <PermissionProtectedRoute path="/sk/nastenka" component={Dashboard} requiredResource="dashboard" />
        <PermissionProtectedRoute path="/en/dashboard" component={Dashboard} requiredResource="dashboard" />
        
        {/* Client routes */}
        <ProtectedRoute path="/clients" component={Clients} />
        <ProtectedRoute path="/klienti" component={Clients} />
        <ProtectedRoute path="/sk/klienti" component={Clients} />
        <ProtectedRoute path="/en/clients" component={Clients} />
        
        {/* Task routes */}
        <ProtectedRoute path="/tasks" component={Tasks} />
        <ProtectedRoute path="/ulohy" component={Tasks} />
        <ProtectedRoute path="/sk/ulohy" component={Tasks} />
        <ProtectedRoute path="/en/tasks" component={Tasks} />
        
        {/* Invoice routes */}
        <ProtectedRoute path="/invoices" component={Invoices} />
        <ProtectedRoute path="/invoices/:id" component={Invoices} />
        <ProtectedRoute path="/faktury" component={Invoices} />
        <ProtectedRoute path="/faktury/:id" component={Invoices} />
        <ProtectedRoute path="/sk/faktury" component={Invoices} />
        <ProtectedRoute path="/sk/faktury/:id" component={Invoices} />
        <ProtectedRoute path="/en/invoices" component={Invoices} />
        <ProtectedRoute path="/en/invoices/:id" component={Invoices} />
        <ProtectedRoute path="/invoices-simple" component={InvoicesSimple} />
        <ProtectedRoute path="/invoices-test" component={InvoicesTest} />
        
        {/* Price quotes routes */}
        <ProtectedRoute path="/price-quotes" component={PriceQuotes} />
        <ProtectedRoute path="/cenove-ponuky" component={PriceQuotes} />
        <ProtectedRoute path="/sk/cenove-ponuky" component={PriceQuotes} />
        <ProtectedRoute path="/en/price-quotes" component={PriceQuotes} />
        
        {/* Credit notes routes */}
        <ProtectedRoute path="/dobropisy" component={DobropisyPage} />
        <ProtectedRoute path="/sk/dobropisy" component={DobropisyPage} />
        <ProtectedRoute path="/en/credit-notes" component={DobropisyPage} />
        
        {/* Reminders routes */}
        <ProtectedRoute path="/reminders" component={RemindersPage} />
        <ProtectedRoute path="/pripomienky" component={RemindersPage} />
        <ProtectedRoute path="/sk/pripomienky" component={RemindersPage} />
        <ProtectedRoute path="/en/reminders" component={RemindersPage} />
        
        {/* Team routes */}
        <ProtectedRoute path="/team" component={TeamManagementPage} />
        <ProtectedRoute path="/tym" component={TeamManagementPage} />
        <ProtectedRoute path="/sk/tym" component={TeamManagementPage} />
        <ProtectedRoute path="/en/team" component={TeamManagementPage} />
        
        {/* Profile routes */}
        <PermissionProtectedRoute path="/profile" component={Profile} requiredResource="profile" requiredAction="view" />
        <PermissionProtectedRoute path="/profil" component={Profile} requiredResource="profile" requiredAction="view" />
        <PermissionProtectedRoute path="/sk/profil" component={Profile} requiredResource="profile" requiredAction="view" />
        <PermissionProtectedRoute path="/en/profile" component={Profile} requiredResource="profile" requiredAction="view" />
        
        {/* Admin routes */}
        <ProtectedRoute 
          path="/admin" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam administráciu..." />}>
              <Admin />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/sk/admin" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam administráciu..." />}>
              <Admin />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/en/admin" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Loading administration..." />}>
              <Admin />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/admin/subscription-plans" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam plány..." />}>
              <SubscriptionPlansPage />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/admin/plany-predplatneho" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam plány..." />}>
              <SubscriptionPlansPage />
            </Suspense>
          )}
        />
        <ProtectedRoute 
          path="/sk/admin/plany-predplatneho" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam plány..." />}>
              <SubscriptionPlansPage />
            </Suspense>
          )}
        />
        <ProtectedRoute 
          path="/en/admin/subscription-plans" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Loading plans..." />}>
              <SubscriptionPlansPage />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/admin/update-center" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam centrum aktualizácií..." />}>
              <UpdateCenterPage />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/sk/admin/centrum-aktualizacii" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam centrum aktualizácií..." />}>
              <UpdateCenterPage />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/en/admin/update-center" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Loading update center..." />}>
              <UpdateCenterPage />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/admin/diagnostics" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam diagnostiku..." />}>
              <AdminDiagnosticsPage />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/sk/admin/diagnostika" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam diagnostiku..." />}>
              <AdminDiagnosticsPage />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/en/admin/diagnostics" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Loading diagnostics..." />}>
              <AdminDiagnosticsPage />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/admin/enterprise" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam enterprise prehľad..." />}>
              <EnterpriseOverview />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/sk/admin/enterprise" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Načítavam enterprise prehľad..." />}>
              <EnterpriseOverview />
            </Suspense>
          )} 
        />
        <ProtectedRoute 
          path="/en/admin/enterprise" 
          component={() => (
            <Suspense fallback={<PageLoadingSpinner text="Loading enterprise overview..." />}>
              <EnterpriseOverview />
            </Suspense>
          )} 
        />
        
        {/* Subscription and pricing routes */}
        <ProtectedRoute path="/upgrade" component={Upgrade} />
        <ProtectedRoute path="/pricing" component={Pricing} />
        <ProtectedRoute path="/cenik" component={Pricing} />
        <ProtectedRoute path="/sk/cenik" component={Pricing} />
        <ProtectedRoute path="/en/pricing" component={Pricing} />
        <ProtectedRoute path="/subscribe" component={Subscribe} />
        <ProtectedRoute path="/predplatne" component={Subscribe} />
        <ProtectedRoute path="/sk/predplatne" component={Subscribe} />
        <ProtectedRoute path="/en/subscribe" component={Subscribe} />

        {/* Planner routes */}
        <ProtectedRoute path="/weekly-planner" component={WeeklyPlanner} />
        <ProtectedRoute path="/tyzdenny-planovac" component={WeeklyPlanner} />
        <ProtectedRoute path="/sk/tyzdenny-planovac" component={WeeklyPlanner} />
        <ProtectedRoute path="/en/weekly-planner" component={WeeklyPlanner} />
        <ProtectedRoute path="/personal-tasks" component={PersonalTasksPage} />
        <ProtectedRoute path="/osobne-ulohy" component={PersonalTasksPage} />
        <ProtectedRoute path="/sk/osobne-ulohy" component={PersonalTasksPage} />
        <ProtectedRoute path="/en/personal-tasks" component={PersonalTasksPage} />
        
        <Route><NotFound /></Route>
      </Switch>
    </MainLayout>
  );
}

function App() {
  // Build timestamp for cache busting: 1756427000
  // Service Worker check - only run once
  useEffect(() => {
    const checkServiceWorkerStatus = async () => {
      console.log('[APP] Starting service worker check...', Date.now());
      
      try {
        // Check for service workers but don't unregister them
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          console.log('[APP] Found service workers:', registrations.length);
          // Don't unregister - let them work normally
        }
        
        // Check caches but don't delete them
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          console.log('[APP] Found caches:', cacheNames.length);
          // Don't delete caches - let ServiceWorker manage them
        }
        
        console.log('[APP] Service worker check completed');
        
      } catch (error) {
        console.error('[APP] Error during service worker check:', error);
      }
    };
    
    // Only run check once
    const hasRun = sessionStorage.getItem('swCheckDone');
    if (!hasRun) {
      sessionStorage.setItem('swCheckDone', 'true');
      checkServiceWorkerStatus();
    }
  }, []);

  return (
    <ApiErrorBoundary>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <SystemSettingsProvider>
            <AuthProvider>
              <OnboardingProvider>
                <ThemeWatcher />
                <Router />
                <Toaster />
                {/* Demo banner disabled */}
                {/* OnboardingTooltipRenderer removed - now handled directly in OnboardingProvider */}
              </OnboardingProvider>
            </AuthProvider>
          </SystemSettingsProvider>
        </QueryClientProvider>
      </I18nProvider>
    </ApiErrorBoundary>
  );
}

export default App;
